import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, FileText, PenLine } from "lucide-react";
import { SignDocument } from "@/components/SignDocument";
import { fmtDate } from "@/lib/dates";

const UPLOADABLE = ["Assigned", "Rejected", "In Progress", "Not Started"];

export const MyPaperwork = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [signing, setSigning] = useState(null);
  const fileRefs = useRef({});
  const load = () => api.get("/paperwork-assignments").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const onFile = async (a, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(a.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("notes", "");
      await api.post(`/paperwork-assignments/${a.id}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${a.name} sent to your coordinator for review`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); e.target.value = ""; }
  };

  const approved = rows.filter((r) => ["Approved", "Waived"].includes(r.status)).length;

  return (
    <Card title="My documents" data-testid="my-paperwork-card"
      action={<span className="text-xs text-zinc-500" data-testid="my-paperwork-progress">{approved}/{rows.length} approved</span>}>
      <p className="text-sm text-zinc-500 mb-3">Download the blank form where there is one, complete it, then upload it here. Your Campus Coordinator reviews each document and marks you all clear.</p>
      {rows.length === 0 ? <EmptyState text="Nothing to upload yet — your coordinator will assign your documents." /> : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 border rounded-md px-3 py-2.5" data-testid={`my-pw-${a.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-zinc-800">{a.name}{a.required === false ? " (optional)" : ""}</p>
                <p className="text-xs text-zinc-400">
                  Due {fmtDate(a.due_date)}
                  {a.file_name ? ` · uploaded ${a.file_name}` : ""}
                  {a.rejection_reason ? ` · ${a.rejection_reason}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.has_template && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/template`} target="_blank" rel="noreferrer"
                    className="text-xs text-cc-blue inline-flex items-center gap-1 hover:underline" data-testid={`my-pw-blank-${a.id}`}>
                    <FileText className="w-3.5 h-3.5" />Blank form
                  </a>
                )}
                <StatusBadge status={a.status} />
                {UPLOADABLE.includes(a.status) && a.sign_in_app && (
                  <Button size="sm" className="h-8 bg-cc-blue text-white" onClick={() => setSigning(a)} data-testid={`my-pw-sign-${a.id}`}>
                    <PenLine className="w-4 h-4 mr-1" />Read &amp; sign
                  </Button>
                )}
                {UPLOADABLE.includes(a.status) && !a.sign_in_app && (
                  <>
                    <input type="file" className="hidden" ref={(el) => (fileRefs.current[a.id] = el)} onChange={(e) => onFile(a, e)}
                      data-testid={`my-pw-input-${a.id}`} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                    <Button size="sm" variant="outline" className="h-8" disabled={busy === a.id}
                      onClick={() => fileRefs.current[a.id]?.click()} data-testid={`my-pw-upload-${a.id}`}>
                      {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />Upload</>}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <SignDocument assignment={signing} onClose={() => setSigning(null)} onSigned={load} />
    </Card>
  );
};
